import * as React from "react"
import { clx, ChevronRightMiniIcon, ChevronLeftIcon } from "~/design-system"
import {
  DayPicker,
  useDayRender,
  type DayPickerRangeProps,
  type DayPickerSingleProps,
  type DayProps,
} from "react-day-picker"
import { buttonVariants } from "~/design-system/components/button"

type OmittedProps = "showWeekNumber" | "captionLayout" | "mode"

type SingleProps = Omit<DayPickerSingleProps, OmittedProps>
type RangeProps = Omit<DayPickerRangeProps, OmittedProps>

/**
 * @interface
 */
type CalendarProps =
  | ({
      mode: "single"
    } & SingleProps)
  | ({
      mode?: undefined
    } & SingleProps)
  | ({
      mode: "range"
    } & RangeProps)

/**
 * This component is based on the [react-date-picker](https://www.npmjs.com/package/react-date-picker) package.
 *
 * @excludeExternal
 */
const Calendar = ({
  /**
   * @ignore
   */
  className,
  /**
   * @ignore
   */
  classNames,
  /**
   * The calendar's mode.
   */
  mode = "single",
  /**
   * Whether to show days of previous and next months.
   *
   * @keep
   */
  showOutsideDays = true,
  /**
   * The locale to use for formatting dates. To change the locale pass a date-fns locale object.
   *
   * @keep
   */
  locale,
  ...props
}: CalendarProps) => {
  return (
    <DayPicker
      mode={mode}
      showOutsideDays={showOutsideDays}
      className={clx(className)}
      classNames={{
        months: "flex flex-col sm:flex-row",
        month: "space-y-2 p-3",
        caption: "flex justify-center relative items-center h-8",
        caption_label:
          "txt-base-regular-plus absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center text-ui-fg-base",
        nav: "space-x-1 flex items-center bg-ui-bg-disabled rounded-md w-full h-full justify-between p-0.5",
        nav_button: buttonVariants({ variant: "secondary", size: "small" }),
        nav_button_previous: "!absolute left-0.5",
        nav_button_next: "!absolute right-0.5",
        table: "w-full border-collapse space-y-1",
        head_row: "flex w-full gap-x-2",
        head_cell: clx(
          "txt-base-regular-plus text-ui-fg-muted m-0 box-border flex h-8 w-8 items-center justify-center p-0"
        ),
        row: "flex w-full mt-2 gap-x-2",
        cell: "txt-base-regular-plus relative rounded-md p-0 text-center focus-within:relative",
        day: "txt-base-regular-plus text-ui-fg-base bg-ui-bg-base hover:bg-ui-bg-base-hover focus-visible:borders-interactive-with-active h-8 w-8 rounded-md p-0 text-center outline-none transition-all",
        day_selected: "bg-blue-dark text-grey-0 hover:bg-blue-dark focus-visible:bg-blue-dark",
        day_outside: "text-ui-fg-faint aria-selected:text-grey-0",
        day_disabled: "text-ui-fg-faint",
        day_range_middle: "aria-selected:!bg-ui-bg-highlight aria-selected:!text-blue-base",
        day_hidden: "invisible",
        ...classNames,
      }}
      locale={locale}
      components={{
        IconLeft: () => <ChevronRightMiniIcon className="rotate-180" />,
        IconRight: () => <ChevronRightMiniIcon />,
        Day: Day,
      }}
      {...(props as SingleProps & RangeProps)}
    />
  )
}
Calendar.displayName = "Calendar"

const Day = ({ date, displayMonth }: DayProps) => {
  const ref = React.useRef<HTMLButtonElement>(null)
  const { activeModifiers, buttonProps, divProps, isButton, isHidden } =
    useDayRender(date, displayMonth, ref)

  const { selected, today, disabled, range_middle } = activeModifiers

  React.useEffect(() => {
    if (selected) {
      ref.current?.focus()
    }
  }, [selected])

  if (isHidden) {
    return <></>
  }

  if (!isButton) {
    return (
      <div
        {...divProps}
        className={clx("flex items-center justify-center", divProps.className)}
      />
    )
  }

  const {
    children: buttonChildren,
    className: buttonClassName,
    ...buttonPropsRest
  } = buttonProps

  return (
    <button
      ref={ref}
      {...buttonPropsRest}
      type="button"
      className={clx("relative", buttonClassName)}
    >
      {buttonChildren}
      {today && (
        <span
          className={clx(
            "absolute right-[5px] top-[5px] h-1 w-1 rounded-full",
            {
              "bg-blue-dark": !selected,
              "bg-grey-0": selected,
              "!bg-blue-dark": selected && range_middle,
              "bg-ui-fg-muted": disabled,
            }
          )}
        />
      )}
    </button>
  )
}

export { Calendar }
